import { html, LitElement } from 'lit';
import { customElement, property, query, queryAll } from 'lit/decorators.js';
import { map } from 'lit/directives/map.js';
import { choose } from 'lit/directives/choose.js';
import { DropdownColorPicker, DropdownList, Input, MenuItem, colors } from '@omegagrid/core';
import { ConditionalFormat, ConditionalFormatIconType, iconSets } from '@omegagrid/grid';
import { msg } from '@omegagrid/localize';
import { StyleForm, RuleThreshold } from '@omegagrid/editor';
import { style } from './ruleForm.style';

@customElement('rp-definition-form-rule')
export class RuleForm extends LitElement {

	static styles = [style];

	@property({type: Object})
	format: ConditionalFormat;

	@query('og-input#condition')
	conditionInput: Input;

	@query('og-dropdown-colorpicker#cmin')
	cminInput: DropdownColorPicker;

	@query('og-dropdown-colorpicker#cmid')
	cmidInput: DropdownColorPicker;

	@query('og-dropdown-colorpicker#cmax')
	cmaxInput: DropdownColorPicker;

	@query('og-dropdown-colorpicker#color')
	colorInput: DropdownColorPicker;

	@query('og-dropdown-list#icons')
	iconList: DropdownList;

	@query('og-editor-form-style#style')
	styleForm: StyleForm;

	@queryAll('og-editor-rule-threshold')
	thresholds: NodeListOf<RuleThreshold>;

	updateFormat() {
		if (!this.format) return;
		let update = false;

		if (this.conditionInput) this.format.condition = this.conditionInput.value;
		else this.format.condition = null;

		if (this.styleForm) this.format.style = this.styleForm.data;
		else this.format.style = null;

		if (this.cminInput) {
			this.format.cmin = this.format.cmin ?? {};
			this.format.cmin.t = 'min';
			this.format.cmin.color = colors.plainHex(this.cminInput.value);
		} else this.format.cmin = null;

		if (this.cmidInput) {
			this.format.cmid = this.format.cmid ?? {};
			this.format.cmid.color = colors.plainHex(this.cmidInput.value);
		} else this.format.cmid = null;

		if (this.cmaxInput) {
			this.format.cmax = this.format.cmax ?? {};
			this.format.cmax.t = 'max';
			this.format.cmax.color = colors.plainHex(this.cmaxInput.value);
		} else this.format.cmax = null;

		if (this.colorInput) this.format.color = colors.plainHex(this.colorInput.value);
		else this.format.color = null;

		if (this.iconList) {
			if (this.format.v != this.iconList.value) update = true;
			this.format.v = this.iconList.value;	
		} else this.format.v = null;

		if (this.thresholds) this.format.thresh = Array.from(this.thresholds).map(t => t.value);
		else this.format.thresh = null;

		if (update) this.requestUpdate();
	}

	renderFormulaForm = () => html`
		<div class="row">${msg('Condition')}</div>

		<div class="row">
			<og-input
				id="condition"
				.value="${this.format.condition || ''}"
				style="width: 100%"
				@change="${() => this.updateFormat()}">
			</og-input>
		</div>

		<div class="row">${msg('Style')}</div>

		<div class="row">
			<og-editor-form-style
				id="style"
				.data="${this.format.style || {}}"
				@change="${() => this.updateFormat()}">
			</og-editor-form-style>
		</div>
	`;

	renderScaleForm = () => html`
		<div class="row">${msg('Colors')}</div>
		<div class="row" style="display: flex; gap: 4px;">
			<og-dropdown-colorpicker
				id="cmin"
				style="flex: 1"
				.value="${colors.css(this.format.cmin?.color)}"
				@change="${() => this.updateFormat()}">
			</og-dropdown-colorpicker>

			<og-dropdown-colorpicker
				id="cmid"
				style="flex: 1"
				.value="${colors.css(this.format.cmid?.color)}"
				@change="${() => this.updateFormat()}">
			</og-dropdown-colorpicker>

			<og-dropdown-colorpicker
				id="cmax"
				style="flex: 1"
				.value="${colors.css(this.format.cmax?.color)}"
				@change="${() => this.updateFormat()}">
			</og-dropdown-colorpicker>
		</div>
	`;

	renderBarForm = () => html`
		<div class="row">${msg('Color')}</div>
		<div class="row" style="display: flex; gap: 4px;">
			<og-dropdown-colorpicker
				id="color"
				style="flex: 1"
				.value="${colors.css(this.format.color)}"
				@change="${() => this.updateFormat()}">
			</og-dropdown-colorpicker>
		</div>
	`;

	iconItemRenderer = (div: HTMLDivElement, index: number, item: MenuItem) => html`
		<div style="padding-left: 5px">
			${map(iconSets.get(item.key as ConditionalFormatIconType), icon => html`
				<og-icon .icon="${icon}"></og-icon>
			`)}&nbsp;(${item.key})
		</div>
	`;

	renderIconForm = () => html`
		<div class="row">${msg('Icons')}</div>
		<div class="row" style="display: flex; gap: 4px;">
			<og-dropdown-list
				id="icons"
				style="flex: 1"
				.items="${Array.from(iconSets.entries()).map(item => ({
					key: item[0],
					value: item[0],
					renderer: this.iconItemRenderer
				}))}"
				.value="${this.format.v as string}"
				@change="${() => this.updateFormat()}">
			</og-dropdown-list>
		</div>
		${this.format.v && iconSets.has(this.format.v as ConditionalFormatIconType) ? html`
			<div class="row">${msg('Thresholds')}</div>
			<div style="display: flex; flex-direction: column; gap: 2px; margin-top: 2px">
				${map(iconSets.get(this.format.v as ConditionalFormatIconType), (_icon, i) => html`
					<og-editor-rule-threshold
						.value="${this.format?.thresh[i]}" 
						?first="${i == 0}"
						@change="${() => this.updateFormat()}">
					</og-editor-rule-threshold>
				`)}
			</div>
		` : ''}
	`;

	render = () => this.format ? html`
		${choose(this.format.type , [
			['formula', this.renderFormulaForm],
			['scale', this.renderScaleForm],
			['bar', this.renderBarForm],
			['icon', this.renderIconForm],
		])}
	` : null;
}
